import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import Column from "./Column";
import "./Columns.css"

const MATCH_EXPLOSION_DURATION_MS = 2000;
const MATCH_ROW_GLOW_DURATION_MS = 500;

const shuffleWords = (words) => {
  const shuffledWords = [...words];
  for (let index = shuffledWords.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(Math.random() * (index + 1));
    [shuffledWords[index], shuffledWords[swapIndex]] = [shuffledWords[swapIndex], shuffledWords[index]];
  }
  return shuffledWords;
};

const hasAlignedMatches = (wordColumns) => {
  if (wordColumns.length < 2) {
    return false;
  }

  const rowCount = Math.min(...wordColumns.map((words) => words.length));
  for (let index = 0; index < rowCount; index += 1) {
    const rowMatchKeys = wordColumns
      .map((words) => words[index]?.matchKey)
      .filter((matchKey) => matchKey !== undefined);
    const uniqueRowMatchKeys = new Set(rowMatchKeys);

    if (uniqueRowMatchKeys.size < rowMatchKeys.length) {
      return true;
    }
  }

  return false;
};

const unalignWordColumns = (wordColumns) => {
  const nextWordColumns = wordColumns.map((words) => [...words]);
  if (nextWordColumns.length < 2) {
    return nextWordColumns;
  }

  const rowCount = Math.min(...nextWordColumns.map((words) => words.length));
  for (let rowIndex = 0; rowIndex < rowCount; rowIndex += 1) {
    const rowMatchKeys = nextWordColumns.map((words) => words[rowIndex]?.matchKey);
    const rowHasDuplicateMatch = rowMatchKeys.some((matchKey, columnIndex) =>
      matchKey !== undefined && rowMatchKeys.indexOf(matchKey) !== columnIndex
    );

    if (!rowHasDuplicateMatch) {
      continue;
    }

    for (let columnIndex = 1; columnIndex < nextWordColumns.length; columnIndex += 1) {
      const duplicateMatchKey = nextWordColumns[columnIndex][rowIndex]?.matchKey;
      const matchKeyIsDuplicated = rowMatchKeys.some((matchKey, candidateColumnIndex) =>
        candidateColumnIndex !== columnIndex && matchKey === duplicateMatchKey
      );

      if (!matchKeyIsDuplicated) {
        continue;
      }

      const swapIndex = nextWordColumns[columnIndex].findIndex((word, candidateIndex) =>
        candidateIndex !== rowIndex &&
        !nextWordColumns.some((columnWords, otherColumnIndex) =>
          otherColumnIndex !== columnIndex &&
          columnWords[candidateIndex]?.matchKey === duplicateMatchKey
        ) &&
        !nextWordColumns.some((columnWords, otherColumnIndex) =>
          otherColumnIndex !== columnIndex &&
          columnWords[candidateIndex]?.matchKey === word?.matchKey
        ) &&
        !rowMatchKeys.some((matchKey, otherColumnIndex) =>
          otherColumnIndex !== columnIndex && matchKey === word?.matchKey
        )
      );

      if (swapIndex !== -1) {
        [nextWordColumns[columnIndex][rowIndex], nextWordColumns[columnIndex][swapIndex]] = [
          nextWordColumns[columnIndex][swapIndex],
          nextWordColumns[columnIndex][rowIndex],
        ];
        break;
      }
    }
  }

  return nextWordColumns;
};

const shuffleNonAnchorColumnsWithoutAlignedMatches = (nextColumnsState, languageData) => {
  if (languageData.length < 2) {
    return nextColumnsState;
  }

  for (let attempt = 0; attempt < 20; attempt += 1) {
    const candidateColumnsState = {
      ...nextColumnsState,
      ...Object.fromEntries(
        languageData.map((language, index) => [
          language.code,
          index === 0
            ? nextColumnsState[language.code] || []
            : shuffleWords(nextColumnsState[language.code] || []),
        ])
      ),
    };
    const wordColumns = languageData.map((language) => candidateColumnsState[language.code] || []);

    if (!hasAlignedMatches(wordColumns)) {
      return candidateColumnsState;
    }
  }

  const unalignedColumns = unalignWordColumns(
    languageData.map((language) => nextColumnsState[language.code] || [])
  );

  return {
    ...nextColumnsState,
    ...Object.fromEntries(
      languageData.map((language, index) => [language.code, unalignedColumns[index] || []])
    ),
  };
};

const moveMatchKeyToIndex = (words, matchKey, targetIndex) => {
  const currentIndex = words.findIndex((word) => word?.matchKey === matchKey);

  if (currentIndex === -1 || targetIndex < 0) {
    return words;
  }

  const nextWords = [...words];
  const [word] = nextWords.splice(currentIndex, 1);
  nextWords.splice(Math.min(targetIndex, nextWords.length), 0, word);
  return nextWords;
};

const alignMatchGroupToIndex = (nextColumnsState, matchKey, languageCodes, targetIndex) => ({
  ...nextColumnsState,
  ...Object.fromEntries(
    languageCodes.map((languageCode) => [
      languageCode,
      moveMatchKeyToIndex(nextColumnsState[languageCode] || [], matchKey, targetIndex),
    ])
  ),
});

const Columns = ({ languages, languageData, wordMatchDictionary, hintsEnabled, soundsEnabled, sendDataFromColumnsToApp }) => {
  const REWARD_MILESTONES = [1, 2, 3, 5, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100];
  const [columnsState, setColumnsState] = useState(
    Object.fromEntries(languageData.map((col) => [col.code, col.words]))
  );
  const [blinkingWords, setBlinkingWords] = useState([]);
  const [glowingWordsByLanguage, setGlowingWordsByLanguage] = useState({});
  const [matchedGroupsByKey, setMatchedGroupsByKey] = useState({});
  const [explodingWordsByLanguage, setExplodingWordsByLanguage] = useState({});
  const [starBursts, setStarBursts] = useState({});
  const [correctWordProgress, setCorrectWordProgress] = useState(0);
  const [progressStartedAt, setProgressStartedAt] = useState(Date.now());
  const [lastPoolIncreaseAt, setLastPoolIncreaseAt] = useState(0);
  const [rewardCelebration, setRewardCelebration] = useState(null);
  const [selectedWordsByColumn, setSelectedWordsByColumn] = useState({});
  const [wordLayoutsByLanguage, setWordLayoutsByLanguage] = useState({});
  const [wordWidthMetricsByLanguage, setWordWidthMetricsByLanguage] = useState({});
  const wordLayoutsByLanguageRef = useRef({});
  const [mouseInactive, setMouseInactive] = useState(false);
  let inactivityTimeout = null;
  let blinkingTimeout = null;

  useEffect(() => {
    setColumnsState(Object.fromEntries(languageData.map((col) => [col.code, col.words])));
    setSelectedWordsByColumn({});
    setGlowingWordsByLanguage({});
    setMatchedGroupsByKey({});
  }, [languageData]);

  const handleWordLayoutSnapshot = (languageCode, layoutsByMatchKey) => {
    wordLayoutsByLanguageRef.current = {
      ...wordLayoutsByLanguageRef.current,
      [languageCode]: layoutsByMatchKey,
    };
    setWordLayoutsByLanguage((current) => ({
      ...current,
      [languageCode]: layoutsByMatchKey,
    }));
  };

  const handleWordWidthMeasurement = (languageCode, widthMetrics) => {
    setWordWidthMetricsByLanguage((current) => ({
      ...current,
      [languageCode]: widthMetrics,
    }));
  };

  const sharedWordWidth = (() => {
    const metrics = languageData
      .map((language) => wordWidthMetricsByLanguage[language.code])
      .filter(Boolean);

    if (metrics.length === 0) {
      return undefined;
    }

    const maxDesiredWidth = Math.max(...metrics.map((metric) => metric.desiredWidth || 0));
    const minAvailableWidth = Math.min(...metrics.map((metric) => metric.availableWidth || Number.MAX_SAFE_INTEGER));
    const nextWidth = Math.min(maxDesiredWidth, minAvailableWidth);

    return nextWidth > 0 ? `${nextWidth}px` : undefined;
  })();

useEffect(() => {
    let blinkingInterval;
    let blinkingTimeout;

    if (!hintsEnabled) {
        setBlinkingWords([]);
        return undefined;
    }

    // ✅ Function to select and blink words
    const triggerBlinking = () => {
        if (languageData.length > 1) {
            const firstColumnWords = languageData[0]?.words || [];
            const secondColumnWords = languageData[1]?.words || [];

            if (firstColumnWords.length && secondColumnWords.length) {
                const randomIndex = Math.floor(Math.random() * firstColumnWords.length);
                const word1 = firstColumnWords[randomIndex];
                const word2 = secondColumnWords.find((candidate) => candidate.matchKey === word1.matchKey);

                if (word2) {
                    console.log("Blinking words:", word1, word2); // ✅ Debugging log
                    setBlinkingWords([
                        { matchKey: word1.matchKey, column: 0 },
                        { matchKey: word2.matchKey, column: 1 },
                    ]);

                    // ✅ Clear blinking after 2 seconds
                    blinkingTimeout = setTimeout(() => {
                        setBlinkingWords([]);
                        console.log("Blinking stopped"); // time blinking
                    }, 2000);
                }
            }
        }
    };

    triggerBlinking();
    blinkingInterval = setInterval(triggerBlinking, 3000);

    // ✅ Cleanup interval and timeout
    return () => {
        clearInterval(blinkingInterval);
        clearTimeout(blinkingTimeout);
        setBlinkingWords([]);
    };
}, [hintsEnabled, languageData, wordMatchDictionary]);




  const handleReorder = (languageCode, updatedWords) => {
    console.log("handleReorder".repeat(20))
    setColumnsState((prevState) => {
      const nextState = {
        ...prevState,
        [languageCode]: updatedWords,
      };

      if (languageData.length <= 2) {
        return nextState;
      }

      const previousWords = prevState[languageCode] || [];
      const stickyGroupEntry = Object.entries(matchedGroupsByKey)
        .filter(([, groupLanguageCodes]) => groupLanguageCodes.includes(languageCode))
        .find(([matchKey]) => {
          const previousIndex = previousWords.findIndex((word) => word?.matchKey === matchKey);
          const nextIndex = updatedWords.findIndex((word) => word?.matchKey === matchKey);
          return previousIndex !== nextIndex;
        });

      if (!stickyGroupEntry) {
        return nextState;
      }

      const [matchKey, groupLanguageCodes] = stickyGroupEntry;
      const nextIndex = updatedWords.findIndex((word) => word?.matchKey === matchKey);

      if (nextIndex === -1) {
        return nextState;
      }

      return alignMatchGroupToIndex(nextState, matchKey, groupLanguageCodes, nextIndex);
    });
  };

  const triggerStarBursts = (matchedCount, rewardCount) => {
    const burstToken = Date.now();
    const nextStarBursts = Object.fromEntries(
      languageData.map((language) => [
        language.code,
        {
          token: burstToken,
          matchedCount,
          streakCount: rewardCount,
        },
      ])
    );

    setStarBursts(nextStarBursts);
    setTimeout(() => {
      setStarBursts({});
    }, 3000);
  };

  const triggerRewardCelebration = (rewardCount, matchedCount) => {
    const token = Date.now();
    const milestoneIndex = REWARD_MILESTONES.indexOf(rewardCount);
    const level = Math.min(milestoneIndex + 1, 6);

    setRewardCelebration({
      token,
      level,
      streakCount: rewardCount,
      matchedCount,
      message: `${rewardCount} Words Correct`,
    });

    setTimeout(() => {
      setRewardCelebration((currentReward) =>
        currentReward?.token === token ? null : currentReward
      );
    }, 2900);
  };

  const getRewardAnimation = (level) => {
    if (level === 1) {
      return {
        initial: { opacity: 0, scale: 0.72, y: 22 },
        animate: { opacity: [0, 1, 1, 0], scale: [0.72, 1.02, 1, 0.92], y: [22, 0, 0, -16] },
      };
    }

    if (level === 2) {
      return {
        initial: { opacity: 0, scale: 0.62, rotate: -5, y: 28 },
        animate: { opacity: [0, 1, 1, 0], scale: [0.62, 1.08, 1.04, 0.86], rotate: [-5, 4, -2, 8], y: [28, -6, -4, -24] },
      };
    }

    if (level === 3) {
      return {
        initial: { opacity: 0, scale: 0.52, rotate: -8, y: 34 },
        animate: { opacity: [0, 1, 1, 0], scale: [0.52, 1.18, 1.1, 0.82], rotate: [-8, 8, -8, 14], y: [34, -10, -8, -38] },
      };
    }

    if (level === 4) {
      return {
        initial: { opacity: 0, scale: 0.42, rotate: -12, y: 42 },
        animate: { opacity: [0, 1, 1, 0], scale: [0.42, 1.26, 1.15, 0.78], rotate: [-12, 10, -10, 18], y: [42, -18, -12, -54] },
      };
    }

    if (level === 5) {
      return {
        initial: { opacity: 0, scale: 0.34, rotate: -16, y: 50 },
        animate: { opacity: [0, 1, 1, 0], scale: [0.34, 1.34, 1.18, 0.72], rotate: [-16, 14, -12, 24], y: [50, -28, -18, -72] },
      };
    }

    return {
      initial: { opacity: 0, scale: 0.28, rotate: -20, y: 58 },
      animate: { opacity: [0, 1, 1, 0], scale: [0.28, 1.44, 1.22, 0.66], rotate: [-20, 18, -16, 30], y: [58, -36, -24, -90] },
    };
  };

  const removeMatchedKeys = (nextColumnsState, matchKeys) => {
    if (languageData.length < 2) {
      return {
        nextColumnsState,
        matchedCount: 0,
        matchedPairs: [],
      };
    }

    const keysToRemove = new Set(matchKeys);

    if (keysToRemove.size === 0) {
      return {
        nextColumnsState,
        matchedCount: 0,
        matchedPairs: [],
      };
    }

    const wordsByLanguageAndMatchKey = Object.fromEntries(
      languageData.map((language) => [
        language.code,
        new Map((nextColumnsState[language.code] || []).map((word) => [word?.matchKey, word])),
      ])
    );
    const matchedPairs = [...keysToRemove]
      .map((matchKey) => {
        const words = Object.fromEntries(
          languageData.map((language) => [
            language.code,
            wordsByLanguageAndMatchKey[language.code].get(matchKey),
          ])
        );

        if (Object.values(words).some((word) => !word)) {
          return null;
        }

        return {
          matchKey,
          words: Object.fromEntries(
            Object.entries(words).map(([languageCode, word]) => [languageCode, word.text])
          ),
        };
      })
      .filter(Boolean);
    const verifiedKeysToRemove = new Set(matchedPairs.map((pair) => pair.matchKey));

    return {
      nextColumnsState: {
        ...nextColumnsState,
        ...Object.fromEntries(
          languageData.map((language) => [
            language.code,
            (nextColumnsState[language.code] || []).filter((word) => !verifiedKeysToRemove.has(word?.matchKey)),
          ])
        ),
      },
      matchedCount: matchedPairs.length,
      matchedPairs,
    };
  };

  const removeExplicitMatch = (nextColumnsState, matchKey) => {
    const matchKeysToRemove = matchKey === undefined ? [] : [matchKey];

    return removeMatchedKeys(nextColumnsState, matchKeysToRemove);
  };

  const removeDroppedAlignedMatch = (nextColumnsState, changedLanguageCode, droppedMatchKey) => {
    if (languageData.length < 2 || droppedMatchKey === undefined) {
      return removeMatchedKeys(nextColumnsState, []);
    }

    const changedWords = nextColumnsState[changedLanguageCode] || [];
    const droppedIndex = changedWords.findIndex((word) => word?.matchKey === droppedMatchKey);

    if (droppedIndex === -1) {
      return removeMatchedKeys(nextColumnsState, []);
    }

    const rowMatchKeys = languageData.map((language) => nextColumnsState[language.code]?.[droppedIndex]?.matchKey);
    const rowMatchesDroppedWord = rowMatchKeys.every((matchKey) => matchKey === droppedMatchKey);
    const matchKeysToRemove = rowMatchesDroppedWord ? [droppedMatchKey] : [];

    return removeMatchedKeys(nextColumnsState, matchKeysToRemove);
  };

  const emitBoardUpdate = (nextColumnsState, matchedCount = 0, matchedPairs = [], layoutOverridesByLanguage = {}) => {
      const now = Date.now();
      const effectiveStartedAt = correctWordProgress === 0 ? now : progressStartedAt;
      const nextProgress = matchedCount > 0 ? correctWordProgress + matchedCount : correctWordProgress;
      const reachedMilestone = REWARD_MILESTONES.filter(
        (milestone) => milestone > correctWordProgress && milestone <= nextProgress
      ).at(-1);
      const enoughTimeForIncrease = matchedCount > 0 && now - lastPoolIncreaseAt >= 3000;
      const multiplierDelta = matchedCount > 0 ? matchedCount * 0.01 : -0.01;
      const boardIncrease = enoughTimeForIncrease && matchedCount > 0 ? 1 : 0;
      const boardDecrease = matchedCount === 0 ? 1 : 0;
      if (matchedPairs.length > 0) {
        const explosionToken = Date.now();
        const nextExplosions = {};

        matchedPairs.forEach((pair) => {
          Object.entries(pair.words || {}).forEach(([languageCode, text]) => {
            if (!nextExplosions[languageCode]) {
              nextExplosions[languageCode] = [];
            }

            const layoutSource =
              layoutOverridesByLanguage[languageCode] ||
              wordLayoutsByLanguageRef.current[languageCode] ||
              wordLayoutsByLanguage[languageCode] ||
              {};

            nextExplosions[languageCode].push({
              token: `${explosionToken}-${languageCode}-${pair.matchKey}`,
              matchKey: pair.matchKey,
              text,
              layout: layoutSource[pair.matchKey] || null,
            });
          });
        });

        setExplodingWordsByLanguage((current) => {
          const merged = { ...current };

          Object.entries(nextExplosions).forEach(([languageCode, explosions]) => {
            merged[languageCode] = [...(merged[languageCode] || []), ...explosions];
          });

          return merged;
        });

        window.setTimeout(() => {
          setExplodingWordsByLanguage((current) => {
            const next = { ...current };

            Object.entries(nextExplosions).forEach(([languageCode, explosions]) => {
              const tokensToRemove = new Set(explosions.map((explosion) => explosion.token));
              next[languageCode] = (next[languageCode] || []).filter(
                (explosion) => !tokensToRemove.has(explosion.token)
              );

              if (next[languageCode].length === 0) {
                delete next[languageCode];
              }
            });

            return next;
          });
        }, MATCH_EXPLOSION_DURATION_MS);
      }
      const boardStateForDisplay = matchedCount > 0
        ? shuffleNonAnchorColumnsWithoutAlignedMatches(nextColumnsState, languageData)
        : nextColumnsState;

      setColumnsState(boardStateForDisplay);
      sendDataFromColumnsToApp(
        {
          languageData: languageData.map((language) => ({
            ...language,
            words: boardStateForDisplay[language.code] || [],
          })),
          matchedCount,
          matchedPairs,
          multiplierDelta,
          boardIncrease,
          boardDecrease,
        }
      );
      setCorrectWordProgress(nextProgress);
      setProgressStartedAt(effectiveStartedAt);
      if (matchedCount > 0 && now - lastPoolIncreaseAt >= 3000) {
        setLastPoolIncreaseAt(now);
      }
      if (reachedMilestone) {
        triggerRewardCelebration(reachedMilestone, matchedCount);
        triggerStarBursts(matchedCount, reachedMilestone);
      }
  };

  const glowThenEmitBoardUpdate = (
    displayColumnsState,
    nextColumnsState,
    matchedCount,
    matchedPairs,
    layoutOverridesByLanguage = {}
  ) => {
    if (matchedCount <= 0 || languageData.length <= 2) {
      emitBoardUpdate(nextColumnsState, matchedCount, matchedPairs, layoutOverridesByLanguage);
      return;
    }

    const glowState = Object.fromEntries(
      languageData.map((language) => [
        language.code,
        matchedPairs.map((pair) => pair.matchKey),
      ])
    );

    setColumnsState(displayColumnsState);
    setSelectedWordsByColumn({});
    setGlowingWordsByLanguage(glowState);
    window.setTimeout(() => {
      setGlowingWordsByLanguage({});
      emitBoardUpdate(nextColumnsState, matchedCount, matchedPairs, layoutOverridesByLanguage);
    }, MATCH_ROW_GLOW_DURATION_MS);
  };

  const glowPartialGroup = (matchKey, languageCodes) => {
    const glowState = Object.fromEntries(
      languageData.map((language) => [
        language.code,
        languageCodes.includes(language.code) ? [matchKey] : [],
      ])
    );

    setGlowingWordsByLanguage(glowState);
    window.setTimeout(() => {
      setGlowingWordsByLanguage({});
    }, MATCH_ROW_GLOW_DURATION_MS);
  };

  const applyStickyMatchGroup = (
    nextColumnsState,
    changedLanguageCode,
    matchKey,
    targetIndex,
    layoutOverridesByLanguage = {},
    forcedLanguageCodes = []
  ) => {
    if (languageData.length <= 2 || matchKey === undefined || targetIndex < 0) {
      return false;
    }

    const existingGroup = matchedGroupsByKey[matchKey] || [];
    const sameRowLanguages = languageData
      .filter((language) => nextColumnsState[language.code]?.[targetIndex]?.matchKey === matchKey)
      .map((language) => language.code);
    const nextGroupLanguages = [...new Set([
      ...existingGroup,
      ...sameRowLanguages,
      changedLanguageCode,
      ...forcedLanguageCodes,
    ])];

    if (nextGroupLanguages.length < 2) {
      return false;
    }

    const alignedColumnsState = alignMatchGroupToIndex(
      nextColumnsState,
      matchKey,
      nextGroupLanguages,
      targetIndex
    );
    const groupIsComplete = nextGroupLanguages.length === languageData.length;

    setSelectedWordsByColumn({});

    if (!groupIsComplete) {
      setMatchedGroupsByKey((currentGroups) => ({
        ...currentGroups,
        [matchKey]: nextGroupLanguages,
      }));
      setColumnsState(alignedColumnsState);
      glowPartialGroup(matchKey, nextGroupLanguages);
      return true;
    }

    setMatchedGroupsByKey((currentGroups) => {
      const nextGroups = { ...currentGroups };
      delete nextGroups[matchKey];
      return nextGroups;
    });

    const { nextColumnsState: nextStateAfterMatches, matchedCount, matchedPairs } =
      removeMatchedKeys(alignedColumnsState, [matchKey]);

    glowThenEmitBoardUpdate(
      alignedColumnsState,
      nextStateAfterMatches,
      matchedCount,
      matchedPairs,
      layoutOverridesByLanguage
    );
    return true;
  };

  const handleWordDrop = (wordDropData) => {
    console.log("handleWordDrop".repeat(20))
    if (Object.keys(wordDropData).includes("action")) {
      if (wordDropData["action"] === "change_language") {
        sendDataFromColumnsToApp(wordDropData);
      }
    } else {
      const nextColumnsState = {
        ...columnsState,
        [wordDropData.column_data.language]: wordDropData.column_data.words,
      };
      setSelectedWordsByColumn((prevState) => ({
        ...prevState,
        [wordDropData.column_data.language]: null,
      }));
      const { nextColumnsState: nextStateAfterMatches, matchedCount, matchedPairs } =
        removeDroppedAlignedMatch(
          nextColumnsState,
          wordDropData.column_data.language,
          wordDropData.dropped_match_key
        );

      const droppedWords = nextColumnsState[wordDropData.column_data.language] || [];
      const droppedIndex = droppedWords.findIndex((word) => word?.matchKey === wordDropData.dropped_match_key);
      const stickyGroupHandledMove = applyStickyMatchGroup(
        nextColumnsState,
        wordDropData.column_data.language,
        wordDropData.dropped_match_key,
        droppedIndex,
        {
          [wordDropData.column_data.language]: wordDropData.column_layouts || {},
        }
      );

      if (stickyGroupHandledMove) {
        return;
      }

      glowThenEmitBoardUpdate(
        nextColumnsState,
        nextStateAfterMatches,
        matchedCount,
        matchedPairs,
        {
          [wordDropData.column_data.language]: wordDropData.column_layouts || {},
        }
      );
    }
  };

  const handleWordSelect = ({ languageCode, matchKey }) => {
    const nextSelectedWordsByColumn = {
      ...selectedWordsByColumn,
      [languageCode]: selectedWordsByColumn[languageCode] === matchKey ? null : matchKey,
    };

    setSelectedWordsByColumn(nextSelectedWordsByColumn);

    if (languageData.length < 2) {
      return;
    }

    const selectedEntries = languageData
      .map((language) => ({
        languageCode: language.code,
        matchKey: nextSelectedWordsByColumn[language.code],
      }))
      .filter((entry) => entry.matchKey !== null && entry.matchKey !== undefined);
    const selectedMatchKeys = languageData.map((language) => nextSelectedWordsByColumn[language.code]);
    const firstSelectedMatchKey = selectedMatchKeys[0];

    if (
      languageData.length > 2 &&
      selectedEntries.length >= 2 &&
      selectedEntries.every((entry) => entry.matchKey === selectedEntries[0].matchKey)
    ) {
      const targetIndex = (columnsState[languageCode] || [])
        .findIndex((word) => word?.matchKey === selectedEntries[0].matchKey);

      applyStickyMatchGroup(
        columnsState,
        languageCode,
        selectedEntries[0].matchKey,
        targetIndex,
        {},
        selectedEntries.map((entry) => entry.languageCode)
      );
      return;
    }

    if (
      firstSelectedMatchKey === null ||
      firstSelectedMatchKey === undefined ||
      selectedMatchKeys.some((matchKey) => matchKey === null || matchKey === undefined) ||
      selectedMatchKeys.some((matchKey) => matchKey !== firstSelectedMatchKey)
    ) {
      return;
    }

    const { nextColumnsState, matchedCount, matchedPairs } = removeExplicitMatch(columnsState, firstSelectedMatchKey);

    if (matchedCount === 0) {
      return;
    }

    setSelectedWordsByColumn({});
    glowThenEmitBoardUpdate(columnsState, nextColumnsState, matchedCount, matchedPairs);
  };
  console.log(blinkingWords)
  return (
    <div className={`columns-container ${languageData.length === 2 ? "two-column-board" : "multi-column-board"}`}>
        <AnimatePresence>
          {rewardCelebration ? (
            <motion.div
              key={rewardCelebration.token}
              initial={getRewardAnimation(rewardCelebration.level).initial}
              animate={getRewardAnimation(rewardCelebration.level).animate}
              exit={{ opacity: 0, scale: 0.74, y: -110 }}
              transition={{ duration: 2.4, ease: [0.22, 1, 0.36, 1] }}
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                zIndex: 10,
                pointerEvents: "none",
                textAlign: "center",
                color: "#fff6b7",
                textShadow: "0 0 18px rgba(255, 223, 92, 0.95), 0 0 36px rgba(255, 120, 0, 0.65)",
                fontWeight: 800,
                letterSpacing: "0.06em",
                whiteSpace: "nowrap",
              }}
            >
              <div style={{ fontSize: `${Math.min(28 + rewardCelebration.level * 6, 60)}px` }}>
                {rewardCelebration.message}
              </div>
              <div style={{ fontSize: `${Math.min(14 + rewardCelebration.level * 2, 24)}px`, marginTop: "8px", opacity: 0.95 }}>
                {rewardCelebration.matchedCount > 1
                  ? `${rewardCelebration.matchedCount} matched this move`
                  : "Keep the streak alive"}
              </div>
            </motion.div>
          ) : null}
        </AnimatePresence>
        {languageData.map((language, index) => (
          <Column
            key={language.code}
            columnNumber={index}
            totalColumns={languageData.length}
            languages={languages}
            keyValue={language.code}
            language_code={language.code}
            title={language.title}
            initialWords={columnsState[language.code]}
            soundsEnabled={soundsEnabled}
            starBurst={starBursts[language.code]}
            onWordsReorder={(updatedWords) => handleReorder(language.code, updatedWords)}
            sendDataToParent={(data) => handleWordDrop(data)}
            allColumnsState={columnsState}
            selectedMatchKey={selectedWordsByColumn[language.code] ?? null}
            onWordSelect={handleWordSelect}
            onWordLayoutSnapshot={handleWordLayoutSnapshot}
            onWordWidthMeasurement={handleWordWidthMeasurement}
            blinkingWords={
              blinkingWords
                .filter((item) => item.column === index)
                .map((item) => item.matchKey)
            }
            glowingWords={glowingWordsByLanguage[language.code] || []}
            explodingWords={explodingWordsByLanguage[language.code] || []}
            sharedWordWidth={sharedWordWidth}
          />
        ))}
    </div>
  );
};

export default Columns;
